import React from 'react';

import { upsertSadhanaEntry, type FetchSadhanaWeekDetailParams } from '../api';
import { isApiError } from '../api';
import type { SadhanaEntryUpsertRequest } from '../types/congregation';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

const STORAGE_KEY = 'kc.iskconkanpur.devoteeSadhanaState.v2';
const STORAGE_VERSION = 2;
const RETRY_INTERVAL_MS = 15_000;

export interface DevoteeSadhanaStreakState {
  current: number;
  longest: number;
  lastEntryDate: string | null;
}

export interface DevoteeSadhanaReminderSettings {
  dailyReminderTime: string | null;
  whatsappOptIn: boolean;
  pushOptIn: boolean;
  quietHours?: [string, string] | null;
}

export type DevoteeSadhanaDraftStatus = 'queued' | 'syncing' | 'error';

export interface DevoteeSadhanaDraft {
  key: string;
  weekId: number;
  itemId: number;
  entryDate: string;
  payload: SadhanaEntryUpsertRequest;
  queuedAt: string;
  status: DevoteeSadhanaDraftStatus;
  attempts: number;
  lastAttemptAt: number | null;
  errorMessage: string | null;
  params?: FetchSadhanaWeekDetailParams;
}

interface DevoteeSadhanaState {
  streak: DevoteeSadhanaStreakState;
  reminders: DevoteeSadhanaReminderSettings;
  drafts: Record<string, DevoteeSadhanaDraft>;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  onboardingCompletedAt: string | null;
  acknowledgedPrescriptionIds: number[];
  mentorCommentViewedAt: string | null;
  lastCelebratedStreak: number;
  inspirationDismissedAt: string | null;
}

const defaultState: DevoteeSadhanaState = {
  streak: { current: 0, longest: 0, lastEntryDate: null },
  reminders: {
    dailyReminderTime: null,
    whatsappOptIn: false,
    pushOptIn: false,
    quietHours: null,
  },
  drafts: {},
  lastSyncedAt: null,
  lastSyncError: null,
  onboardingCompletedAt: null,
  acknowledgedPrescriptionIds: [],
  mentorCommentViewedAt: null,
  lastCelebratedStreak: 0,
  inspirationDismissedAt: null,
};

type DevoteeSadhanaAction =
  | { type: 'QUEUE_DRAFT'; draft: Omit<DevoteeSadhanaDraft, 'status' | 'queuedAt' | 'attempts' | 'lastAttemptAt' | 'errorMessage'> }
  | { type: 'SET_DRAFT_STATUS'; key: string; status: DevoteeSadhanaDraftStatus; errorMessage?: string | null }
  | { type: 'REMOVE_DRAFT'; key: string }
  | { type: 'SET_REMINDERS'; updates: Partial<DevoteeSadhanaReminderSettings> }
  | { type: 'RECORD_SUBMISSION'; entryDate: string }
  | { type: 'SET_LAST_SYNC_RESULT'; lastSyncedAt: string | null; lastSyncError: string | null }
  | { type: 'COMPLETE_ONBOARDING'; completedAt: string }
  | { type: 'ACKNOWLEDGE_PRESCRIPTION'; prescriptionId: number }
  | { type: 'MARK_MENTOR_COMMENT_VIEWED'; viewedAt: string }
  | { type: 'SET_LAST_CELEBRATED_STREAK'; streak: number }
  | { type: 'DISMISS_INSPIRATION'; dismissedAt: string }
  | { type: 'RESTORE_INSPIRATION' };

const persistState = (state: DevoteeSadhanaState) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    const payload = JSON.stringify({ version: STORAGE_VERSION, state });
    window.localStorage.setItem(STORAGE_KEY, payload);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Unable to persist devotee sadhana state', error);
  }
};

const loadState = (): DevoteeSadhanaState => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultState;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultState;
    }
    const parsed = JSON.parse(raw) as { version?: number; state?: DevoteeSadhanaState } | null;
    if (!parsed || parsed.version !== STORAGE_VERSION || !parsed.state) {
      return defaultState;
    }
    return {
      ...defaultState,
      ...parsed.state,
      drafts: parsed.state.drafts ?? {},
      acknowledgedPrescriptionIds: parsed.state.acknowledgedPrescriptionIds ?? [],
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Unable to load devotee sadhana state', error);
    return defaultState;
  }
};

const toUnixDay = (value: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  return Math.floor(date.getTime() / (24 * 60 * 60 * 1000));
};

const calculateNextStreak = (state: DevoteeSadhanaState, entryDate: string): DevoteeSadhanaStreakState => {
  if (!entryDate) {
    return state.streak;
  }
  const { lastEntryDate, current, longest } = state.streak;
  if (!lastEntryDate) {
    const first = { current: 1, longest: Math.max(1, longest), lastEntryDate: entryDate };
    return first;
  }
  const lastDay = toUnixDay(lastEntryDate);
  const currentDay = toUnixDay(entryDate);
  if (currentDay === lastDay) {
    return state.streak.lastEntryDate === entryDate
      ? state.streak
      : { ...state.streak, lastEntryDate: entryDate };
  }
  if (currentDay - lastDay === 1) {
    const nextCurrent = current + 1;
    return {
      current: nextCurrent,
      longest: Math.max(longest, nextCurrent),
      lastEntryDate: entryDate,
    };
  }
  return {
    current: 1,
    longest: Math.max(longest, 1),
    lastEntryDate: entryDate,
  };
};

const devoteeSadhanaReducer = (
  state: DevoteeSadhanaState,
  action: DevoteeSadhanaAction,
): DevoteeSadhanaState => {
  switch (action.type) {
    case 'QUEUE_DRAFT': {
      const key = action.draft.key;
      const existing = state.drafts[key];
      const queuedAt = new Date().toISOString();
      return {
        ...state,
        drafts: {
          ...state.drafts,
          [key]: {
            key,
            weekId: action.draft.weekId,
            itemId: action.draft.itemId,
            entryDate: action.draft.entryDate,
            payload: action.draft.payload,
            params: action.draft.params,
            queuedAt,
            status: 'queued',
            attempts: existing?.attempts ?? 0,
            lastAttemptAt: existing?.lastAttemptAt ?? null,
            errorMessage: null,
          },
        },
      };
    }
    case 'SET_DRAFT_STATUS': {
      const draft = state.drafts[action.key];
      if (!draft) {
        return state;
      }
      return {
        ...state,
        drafts: {
          ...state.drafts,
          [action.key]: {
            ...draft,
            status: action.status,
            attempts: action.status === 'syncing' ? draft.attempts + 1 : draft.attempts,
            lastAttemptAt: Date.now(),
            errorMessage: action.status === 'error' ? action.errorMessage ?? 'Unable to sync entry' : null,
          },
        },
      };
    }
    case 'REMOVE_DRAFT': {
      if (!state.drafts[action.key]) {
        return state;
      }
      const nextDrafts = { ...state.drafts };
      delete nextDrafts[action.key];
      return {
        ...state,
        drafts: nextDrafts,
      };
    }
    case 'SET_REMINDERS': {
      return {
        ...state,
        reminders: {
          ...state.reminders,
          ...action.updates,
        },
      };
    }
    case 'RECORD_SUBMISSION': {
      return {
        ...state,
        streak: calculateNextStreak(state, action.entryDate),
      };
    }
    case 'SET_LAST_SYNC_RESULT': {
      return {
        ...state,
        lastSyncedAt: action.lastSyncedAt,
        lastSyncError: action.lastSyncError,
      };
    }
    case 'COMPLETE_ONBOARDING': {
      return {
        ...state,
        onboardingCompletedAt: action.completedAt,
      };
    }
    case 'ACKNOWLEDGE_PRESCRIPTION': {
      if (state.acknowledgedPrescriptionIds.includes(action.prescriptionId)) {
        return state;
      }
      return {
        ...state,
        acknowledgedPrescriptionIds: [...state.acknowledgedPrescriptionIds, action.prescriptionId],
      };
    }
    case 'MARK_MENTOR_COMMENT_VIEWED': {
      return {
        ...state,
        mentorCommentViewedAt: action.viewedAt,
      };
    }
    case 'SET_LAST_CELEBRATED_STREAK': {
      return {
        ...state,
        lastCelebratedStreak: action.streak,
      };
    }
    case 'DISMISS_INSPIRATION': {
      return {
        ...state,
        inspirationDismissedAt: action.dismissedAt,
      };
    }
    case 'RESTORE_INSPIRATION': {
      return {
        ...state,
        inspirationDismissedAt: null,
      };
    }
    default:
      return state;
  }
};

export interface DevoteeSadhanaContextValue {
  streak: DevoteeSadhanaStreakState;
  reminderSettings: DevoteeSadhanaReminderSettings;
  drafts: Record<string, DevoteeSadhanaDraft>;
  pendingDrafts: DevoteeSadhanaDraft[];
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  onboardingCompletedAt: string | null;
  acknowledgedPrescriptionIds: number[];
  mentorCommentViewedAt: string | null;
  lastCelebratedStreak: number;
  inspirationDismissedAt: string | null;
  queueDraft: (draft: Omit<DevoteeSadhanaDraft, 'status' | 'queuedAt' | 'attempts' | 'lastAttemptAt' | 'errorMessage'>) => string;
  removeDraft: (key: string) => void;
  updateReminderSettings: (updates: Partial<DevoteeSadhanaReminderSettings>) => void;
  recordSubmission: (entryDate: string) => void;
  completeOnboarding: () => void;
  acknowledgePrescription: (prescriptionId: number) => void;
  markMentorCommentViewed: () => void;
  markStreakCelebrated: (streak: number) => void;
  dismissInspiration: () => void;
  restoreInspiration: () => void;
}

const DevoteeSadhanaContext = React.createContext<DevoteeSadhanaContextValue | undefined>(undefined);

export const DevoteeSadhanaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = React.useReducer(devoteeSadhanaReducer, defaultState, () => loadState());
  const network = useNetworkStatus();
  const [retryTick, setRetryTick] = React.useState(0);

  React.useEffect(() => {
    persistState(state);
  }, [state]);

  const pendingDrafts = React.useMemo(
    () =>
      Object.values(state.drafts).filter((draft) => {
        if (draft.status === 'queued') {
          return true;
        }
        if (draft.status === 'error') {
          if (!draft.lastAttemptAt) {
            return true;
          }
          return Date.now() - draft.lastAttemptAt > RETRY_INTERVAL_MS;
        }
        return false;
      }),
    [state.drafts],
  );

  const nextErrorRetryInMs = React.useMemo(() => {
    const now = Date.now();
    const candidates = Object.values(state.drafts)
      .filter((draft) => draft.status === 'error' && draft.lastAttemptAt)
      .map((draft) => Math.max(0, RETRY_INTERVAL_MS - (now - (draft.lastAttemptAt ?? 0))));
    if (candidates.length === 0) {
      return null;
    }
    return Math.min(...candidates);
  }, [state.drafts]);

  React.useEffect(() => {
    if (!network.online) {
      return;
    }
    if (nextErrorRetryInMs === null) {
      return;
    }
    if (nextErrorRetryInMs === 0) {
      setRetryTick(Date.now());
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const timer = window.setTimeout(() => setRetryTick(Date.now()), nextErrorRetryInMs);
    return () => window.clearTimeout(timer);
  }, [network.online, nextErrorRetryInMs]);

  React.useEffect(() => {
    if (!network.online || pendingDrafts.length === 0) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();

    const syncDrafts = async () => {
      for (const draft of pendingDrafts) {
        if (cancelled) {
          break;
        }
        dispatch({ type: 'SET_DRAFT_STATUS', key: draft.key, status: 'syncing' });
        try {
          await upsertSadhanaEntry(draft.weekId, draft.payload, draft.params, controller.signal);
          if (cancelled) {
            break;
          }
          dispatch({ type: 'REMOVE_DRAFT', key: draft.key });
          dispatch({ type: 'SET_LAST_SYNC_RESULT', lastSyncedAt: new Date().toISOString(), lastSyncError: null });
        } catch (error) {
          if (cancelled) {
            break;
          }
          if ((error as { name?: string }).name === 'AbortError') {
            dispatch({ type: 'SET_DRAFT_STATUS', key: draft.key, status: 'queued' });
            break;
          }
          const message = isApiError(error) ? error.message : 'Unable to sync entry';
          dispatch({ type: 'SET_DRAFT_STATUS', key: draft.key, status: 'error', errorMessage: message });
          dispatch({
            type: 'SET_LAST_SYNC_RESULT',
            lastSyncedAt: state.lastSyncedAt,
            lastSyncError: message,
          });
        }
      }
    };

    void syncDrafts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [network.online, pendingDrafts, state.lastSyncedAt, retryTick]);

  const queueDraft = React.useCallback<DevoteeSadhanaContextValue['queueDraft']>(
    (draft) => {
      const key = draft.key;
      dispatch({ type: 'QUEUE_DRAFT', draft: { ...draft, key } });
      return key;
    },
    [],
  );

  const removeDraft = React.useCallback<DevoteeSadhanaContextValue['removeDraft']>((key) => {
    dispatch({ type: 'REMOVE_DRAFT', key });
  }, []);

  const updateReminderSettings = React.useCallback<DevoteeSadhanaContextValue['updateReminderSettings']>((updates) => {
    dispatch({ type: 'SET_REMINDERS', updates });
  }, []);

  const recordSubmission = React.useCallback<DevoteeSadhanaContextValue['recordSubmission']>((entryDate) => {
    if (!entryDate) {
      return;
    }
    dispatch({ type: 'RECORD_SUBMISSION', entryDate });
  }, []);

  const completeOnboarding = React.useCallback<DevoteeSadhanaContextValue['completeOnboarding']>(() => {
    dispatch({ type: 'COMPLETE_ONBOARDING', completedAt: new Date().toISOString() });
  }, []);

  const acknowledgePrescription = React.useCallback<DevoteeSadhanaContextValue['acknowledgePrescription']>((prescriptionId) => {
    dispatch({ type: 'ACKNOWLEDGE_PRESCRIPTION', prescriptionId });
  }, []);

  const markMentorCommentViewed = React.useCallback<DevoteeSadhanaContextValue['markMentorCommentViewed']>(() => {
    dispatch({ type: 'MARK_MENTOR_COMMENT_VIEWED', viewedAt: new Date().toISOString() });
  }, []);

  const markStreakCelebrated = React.useCallback<DevoteeSadhanaContextValue['markStreakCelebrated']>((streak) => {
    dispatch({ type: 'SET_LAST_CELEBRATED_STREAK', streak });
  }, []);

  const dismissInspiration = React.useCallback<DevoteeSadhanaContextValue['dismissInspiration']>(() => {
    dispatch({ type: 'DISMISS_INSPIRATION', dismissedAt: new Date().toISOString() });
  }, []);

  const restoreInspiration = React.useCallback<DevoteeSadhanaContextValue['restoreInspiration']>(() => {
    dispatch({ type: 'RESTORE_INSPIRATION' });
  }, []);

  const value = React.useMemo<DevoteeSadhanaContextValue>(
    () => ({
      streak: state.streak,
      reminderSettings: state.reminders,
      drafts: state.drafts,
      pendingDrafts,
      lastSyncedAt: state.lastSyncedAt,
      lastSyncError: state.lastSyncError,
      onboardingCompletedAt: state.onboardingCompletedAt,
      acknowledgedPrescriptionIds: state.acknowledgedPrescriptionIds,
      mentorCommentViewedAt: state.mentorCommentViewedAt,
      lastCelebratedStreak: state.lastCelebratedStreak,
      inspirationDismissedAt: state.inspirationDismissedAt,
      queueDraft,
      removeDraft,
      updateReminderSettings,
      recordSubmission,
      completeOnboarding,
      acknowledgePrescription,
      markMentorCommentViewed,
      markStreakCelebrated,
      dismissInspiration,
      restoreInspiration,
    }),
    [
      state.streak,
      state.reminders,
      state.drafts,
      pendingDrafts,
      state.lastSyncedAt,
      state.lastSyncError,
      state.onboardingCompletedAt,
      state.acknowledgedPrescriptionIds,
      state.mentorCommentViewedAt,
      state.lastCelebratedStreak,
      state.inspirationDismissedAt,
      queueDraft,
      removeDraft,
      updateReminderSettings,
      recordSubmission,
      completeOnboarding,
      acknowledgePrescription,
      markMentorCommentViewed,
      markStreakCelebrated,
      dismissInspiration,
      restoreInspiration,
    ],
  );

  return <DevoteeSadhanaContext.Provider value={value}>{children}</DevoteeSadhanaContext.Provider>;
};

export const useDevoteeSadhanaContext = (): DevoteeSadhanaContextValue => {
  const context = React.useContext(DevoteeSadhanaContext);
  if (!context) {
    throw new Error('useDevoteeSadhanaContext must be used within a DevoteeSadhanaProvider');
  }
  return context;
};

export const buildDraftKey = (weekId: number, itemId: number, entryDate: string) => `${weekId}:${itemId}:${entryDate}`;
